import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  Cpu,
  Coins,
  Users,
  RefreshCw,
  Clock,
  Sparkles,
  ShieldCheck,
  TrendingUp,
  FileText,
  Search,
  CheckCircle2,
  Info
} from 'lucide-react';

interface MetricSummary {
  totalRequests: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number | null;
  estimatedCostFormatted: string;
  activeAiUsers: number;
  averageLatencyMs: number;
  successRate: number;
}

interface FeatureBreakdown {
  feature: string;
  label: string;
  requests: number;
  totalTokens: number;
  cost: number | null;
  formattedCost: string;
  avgLatencyMs: number;
}

interface ProviderBreakdown {
  userId: string;
  providerName: string;
  role: string;
  requests: number;
  totalTokens: number;
  cost: number | null;
  formattedCost: string;
}

interface DocTypeBreakdown {
  documentType: string;
  label: string;
  requests: number;
  totalTokens: number;
  cost: number | null;
  formattedCost: string;
}

interface DailyTrendItem {
  date: string;
  requests: number;
  totalTokens: number;
  cost: number | null;
  formattedCost: string;
}

interface AiUsageData {
  range: 'TODAY' | '7_DAYS' | '30_DAYS' | 'THIS_MONTH';
  startDate: string;
  endDate: string;
  metrics: MetricSummary;
  averages: {
    avgCostSoapAssist: number | null;
    avgCostSoapAssistFormatted: string;
    avgCostEvalAssist: number | null;
    avgCostEvalAssistFormatted: string;
    avgCostPocQprAssist: number | null;
    avgCostPocQprAssistFormatted: string;
    highestCostFeature: {
      feature: string;
      cost: number | null;
      formattedCost: string;
      requests: number;
    } | null;
  };
  breakdowns: {
    byFeature: FeatureBreakdown[];
    byProvider: ProviderBreakdown[];
    byDocumentType: DocTypeBreakdown[];
    dailyTrend: DailyTrendItem[];
  };
}

export const OwnerAiDataUsageView: React.FC = () => {
  const [range, setRange] = useState<'TODAY' | '7_DAYS' | '30_DAYS' | 'THIS_MONTH'>('30_DAYS');
  const [data, setData] = useState<AiUsageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsageData = useCallback(async (selectedRange: typeof range) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/owner/ai-usage?range=${selectedRange}`);
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('Owner workspace authorization required.');
        }
        throw new Error(`Failed to load AI usage telemetry (${res.status})`);
      }
      const json: AiUsageData = await res.json();
      setData(json);
    } catch (err: any) {
      setError(err.message || 'Unable to retrieve AI usage telemetry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsageData(range);
  }, [range, fetchUsageData]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div id="owner-ai-usage-view" className="space-y-6 pb-20 max-w-7xl mx-auto">
      {/* Top Header Card: Canonical rich navy / blue-grey shell */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-700/80 desktop-header-shell p-6 shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
        {/* Subtle specular top hairline */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center space-x-2.5">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                <Activity className="w-4 h-4" />
              </div>
              <h1 className="text-xl font-bold text-white tracking-tight">Pulse AI & Data Usage Monitor</h1>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide bg-amber-500/10 border border-amber-500/20 text-amber-400">
                OWNER DESKTOP
              </span>
            </div>
            <p className="text-xs text-slate-400 max-w-2xl">
              Internal capacity view tracking Pulse AI requests, token consumption, and estimated Gemini API provider costs. Grounded strictly in actual token metadata.
            </p>
          </div>

          {/* Range Selector & Refresh */}
          <div className="flex items-center space-x-2 shrink-0">
            <div className="inline-flex rounded-xl bg-slate-950 p-1 border border-slate-800 text-xs">
              {(
                [
                  { id: 'TODAY', label: 'Today' },
                  { id: '7_DAYS', label: '7 Days' },
                  { id: '30_DAYS', label: '30 Days' },
                  { id: 'THIS_MONTH', label: 'This Month' }
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  id={`ai-usage-range-${tab.id.toLowerCase()}`}
                  type="button"
                  onClick={() => setRange(tab.id)}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer ${
                    range === tab.id
                      ? 'bg-amber-500 text-slate-950 font-semibold shadow-sm'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <button
              id="ai-usage-refresh-btn"
              type="button"
              onClick={() => fetchUsageData(range)}
              disabled={loading}
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 transition-colors disabled:opacity-50 cursor-pointer"
              title="Refresh telemetry"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-amber-400' : ''}`} />
            </button>
          </div>
        </div>

        {/* Telemetry Status Bar */}
        <div className="mt-4 pt-4 border-t border-slate-800/80 flex flex-wrap items-center justify-between text-xs text-slate-400 gap-2">
          <div className="flex items-center space-x-2">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-slate-300 font-medium">Non-blocking Telemetry Active</span>
            <span className="text-slate-600">•</span>
            <span>Zero impact on clinical latency or AI completion flow</span>
          </div>

          <div className="flex items-center space-x-1.5 text-slate-500 text-[11px]">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span>Strictly telemetry only: No patient health information or prompt prose stored</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-950/40 border border-rose-800/60 text-rose-300 text-xs flex items-center justify-between">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => fetchUsageData(range)}
            className="underline hover:text-rose-200 cursor-pointer ml-4 font-semibold"
          >
            Retry
          </button>
        </div>
      )}

      {/* Primary KPI Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: AI Requests */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5 shadow-lg relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">AI Requests</span>
            <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
              <Sparkles className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline space-x-2">
            <span className="text-3xl font-extrabold text-white tracking-tight">
              {loading && !data ? '—' : formatNumber(data?.metrics.totalRequests || 0)}
            </span>
            <span className="text-xs text-slate-400 font-medium">requests</span>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400">
            <span className="flex items-center space-x-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              <span>{data?.metrics.successRate ?? 100}% success</span>
            </span>
            <span className="text-slate-400">Avg {data?.metrics.averageLatencyMs ?? 0}ms</span>
          </div>
        </div>

        {/* Card 2: Total Tokens */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5 shadow-lg relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Tokens</span>
            <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
              <Cpu className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline space-x-2">
            <span className="text-3xl font-extrabold text-white tracking-tight">
              {loading && !data ? '—' : formatNumber(data?.metrics.totalTokens || 0)}
            </span>
            <span className="text-xs text-slate-400 font-medium">tokens</span>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400">
            <span>In: {formatNumber(data?.metrics.inputTokens || 0)}</span>
            <span className="text-slate-600">•</span>
            <span>Out: {formatNumber(data?.metrics.outputTokens || 0)}</span>
          </div>
        </div>

        {/* Card 3: Estimated AI Cost */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5 shadow-lg relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Estimated AI Cost</span>
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <Coins className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline space-x-2">
            <span className="text-3xl font-extrabold text-emerald-400 tracking-tight">
              {loading && !data ? '—' : data?.metrics.estimatedCostFormatted ?? 'Cost estimate unavailable'}
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400">
            <span>Model: Gemini 3.8 Flash</span>
            <span className="text-[10px] text-slate-500">Official API Rates</span>
          </div>
        </div>

        {/* Card 4: Active AI Users */}
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5 shadow-lg relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Active AI Users</span>
            <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
              <Users className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline space-x-2">
            <span className="text-3xl font-extrabold text-white tracking-tight">
              {loading && !data ? '—' : data?.metrics.activeAiUsers || 0}
            </span>
            <span className="text-xs text-slate-400 font-medium">providers</span>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400">
            <span>Window: {data ? `${data.startDate} to ${data.endDate}` : '—'}</span>
          </div>
        </div>
      </div>

      {/* Unit Economics & Averages Card */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-lg">
        <div className="flex items-center space-x-2 mb-4">
          <TrendingUp className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-bold text-white tracking-tight uppercase tracking-wider">
            Unit Economics & Workload Averages
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
          <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-4">
            <span className="text-slate-400 text-[11px] font-medium block">Avg Cost / SOAP Assist</span>
            <div className="mt-1 text-lg font-bold text-white">
              {data?.averages.avgCostSoapAssistFormatted ?? 'Cost estimate unavailable'}
            </div>
            <span className="text-[10px] text-slate-500 mt-1 block">Per drafted daily SOAP note</span>
          </div>

          <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-4">
            <span className="text-slate-400 text-[11px] font-medium block">Avg Cost / Eval Assist</span>
            <div className="mt-1 text-lg font-bold text-white">
              {data?.averages.avgCostEvalAssistFormatted ?? 'Cost estimate unavailable'}
            </div>
            <span className="text-[10px] text-slate-500 mt-1 block">Comprehensive Evaluation sections</span>
          </div>

          <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-4">
            <span className="text-slate-400 text-[11px] font-medium block">Avg Cost / POC & QPR Assist</span>
            <div className="mt-1 text-lg font-bold text-white">
              {data?.averages.avgCostPocQprAssistFormatted ?? 'Cost estimate unavailable'}
            </div>
            <span className="text-[10px] text-slate-500 mt-1 block">Plan of Care / QPR synthesis</span>
          </div>

          <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-4">
            <span className="text-slate-400 text-[11px] font-medium block">Highest-Cost Feature</span>
            <div className="mt-1 text-lg font-bold text-amber-400 truncate">
              {data?.averages.highestCostFeature?.feature || 'None'}
            </div>
            <span className="text-[10px] text-slate-500 mt-1 block">
              {data?.averages.highestCostFeature
                ? `${data.averages.highestCostFeature.formattedCost} (${data.averages.highestCostFeature.requests} reqs)`
                : 'No usage recorded'}
            </span>
          </div>
        </div>
      </div>

      {/* Grid: Usage by Feature + Usage by Document Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage by Feature */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <h3 className="text-sm font-bold text-white tracking-tight">Usage by Feature Surface</h3>
              </div>
              <span className="text-[11px] text-slate-400">{data?.breakdowns.byFeature.length || 0} surfaces</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 text-[11px]">
                    <th className="pb-2.5 font-medium">Feature</th>
                    <th className="pb-2.5 font-medium text-right">Requests</th>
                    <th className="pb-2.5 font-medium text-right">Tokens</th>
                    <th className="pb-2.5 font-medium text-right">Est. Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {data?.breakdowns.byFeature.map((item) => (
                    <tr key={item.feature} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-2.5 font-medium text-white flex items-center space-x-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
                        <span>{item.label}</span>
                      </td>
                      <td className="py-2.5 text-right text-slate-300">{formatNumber(item.requests)}</td>
                      <td className="py-2.5 text-right text-slate-400">{formatNumber(item.totalTokens)}</td>
                      <td className="py-2.5 text-right font-medium text-emerald-400">{item.formattedCost}</td>
                    </tr>
                  ))}
                  {(!data || data.breakdowns.byFeature.length === 0) && (
                    <tr>
                      <td colSpan={4} className="py-4 text-center text-slate-500">
                        No AI feature events recorded in this time range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Usage by Document Type */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-indigo-400" />
                <h3 className="text-sm font-bold text-white tracking-tight">Usage by Document Type</h3>
              </div>
              <span className="text-[11px] text-slate-400">{data?.breakdowns.byDocumentType.length || 0} types</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 text-[11px]">
                    <th className="pb-2.5 font-medium">Document / Query Type</th>
                    <th className="pb-2.5 font-medium text-right">Requests</th>
                    <th className="pb-2.5 font-medium text-right">Tokens</th>
                    <th className="pb-2.5 font-medium text-right">Est. Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {data?.breakdowns.byDocumentType.map((item) => (
                    <tr key={item.documentType} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-2.5 font-medium text-white flex items-center space-x-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                        <span>{item.label}</span>
                      </td>
                      <td className="py-2.5 text-right text-slate-300">{formatNumber(item.requests)}</td>
                      <td className="py-2.5 text-right text-slate-400">{formatNumber(item.totalTokens)}</td>
                      <td className="py-2.5 text-right font-medium text-emerald-400">{item.formattedCost}</td>
                    </tr>
                  ))}
                  {(!data || data.breakdowns.byDocumentType.length === 0) && (
                    <tr>
                      <td colSpan={4} className="py-4 text-center text-slate-500">
                        No document-linked AI calls recorded in this time range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Grid: Usage by Provider + Daily Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage by Provider */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-bold text-white tracking-tight">Usage by Provider</h3>
              </div>
              <span className="text-[11px] text-slate-400">{data?.breakdowns.byProvider.length || 0} providers</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 text-[11px]">
                    <th className="pb-2.5 font-medium">Provider & Role</th>
                    <th className="pb-2.5 font-medium text-right">Requests</th>
                    <th className="pb-2.5 font-medium text-right">Tokens</th>
                    <th className="pb-2.5 font-medium text-right">Est. Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {data?.breakdowns.byProvider.map((item) => (
                    <tr key={item.userId} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-2.5 font-medium text-white">
                        <div className="font-semibold text-white">{item.providerName}</div>
                        <div className="text-[10px] text-slate-400">{item.role}</div>
                      </td>
                      <td className="py-2.5 text-right text-slate-300">{formatNumber(item.requests)}</td>
                      <td className="py-2.5 text-right text-slate-400">{formatNumber(item.totalTokens)}</td>
                      <td className="py-2.5 text-right font-medium text-emerald-400">{item.formattedCost}</td>
                    </tr>
                  ))}
                  {(!data || data.breakdowns.byProvider.length === 0) && (
                    <tr>
                      <td colSpan={4} className="py-4 text-center text-slate-500">
                        No provider AI activity recorded in this time range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Daily Trend */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-lg flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-cyan-400" />
                <h3 className="text-sm font-bold text-white tracking-tight">Daily Usage & Cost Trend</h3>
              </div>
              <span className="text-[11px] text-slate-400">{data?.breakdowns.dailyTrend.length || 0} days</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 text-[11px]">
                    <th className="pb-2.5 font-medium">Date</th>
                    <th className="pb-2.5 font-medium text-right">Requests</th>
                    <th className="pb-2.5 font-medium text-right">Tokens</th>
                    <th className="pb-2.5 font-medium text-right">Daily Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {data?.breakdowns.dailyTrend.map((item) => (
                    <tr key={item.date} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-2.5 font-medium text-white">{item.date}</td>
                      <td className="py-2.5 text-right text-slate-300">{formatNumber(item.requests)}</td>
                      <td className="py-2.5 text-right text-slate-400">{formatNumber(item.totalTokens)}</td>
                      <td className="py-2.5 text-right font-medium text-emerald-400">{item.formattedCost}</td>
                    </tr>
                  ))}
                  {(!data || data.breakdowns.dailyTrend.length === 0) && (
                    <tr>
                      <td colSpan={4} className="py-4 text-center text-slate-500">
                        No telemetry events recorded in selected window.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance & Policy Footer */}
      <div className="bg-slate-950/60 border border-slate-800/70 rounded-2xl p-4 flex items-start space-x-3 text-xs text-slate-400">
        <Info className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <span className="font-semibold text-slate-200">Owner Telemetry & Cost Planning Policy</span>
          <p className="text-[11px] leading-relaxed text-slate-400">
            This workspace monitor provides operational transparency into company-level AI API consumption and token volume.
            No fees, deductions, or subscriptions are levied against clinicians. Telemetry capture is entirely asynchronous
            and failure-isolated to guarantee clinical documentation is never delayed or disrupted.
          </p>
        </div>
      </div>
    </div>
  );
};
